import React, { useState, useEffect, useRef } from 'react';
import { openStripeCheckout } from '../../utils/stripeRedirect';
import { useNavigate } from 'react-router-dom';
import apiClient from '../../utils/apiClient';
import {
  CreditCard, Loader2, Clock, ChevronRight, ChevronDown, Zap, Shield, X, Check,
  Crown, Star, Heart, Award, ArrowRight, Users, Mail, Sparkles, Sun
} from 'lucide-react';
import { isDiscountTier, verifiesBeforeCheckout, verificationDocs, tierDisplayName, ageLabel, hasAgeWindow, verificationBadge, discountTiersBlurb, maxCycleSaving } from '../../utils/planRules';
import { isNative } from '../../services/native';
import { restoreIAPPurchases } from '../../services/iap';
import { useIAPPurchase } from '../../hooks/useIAPPurchase';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { toast } from '../../utils/toast';
import { API_URL } from '../../config';
import { suspendAutoLogout } from '../../utils/autoLogoutSuspend';
import { FreeModeBanner } from '../FreeModeBanner';
import { useAuth } from '../../contexts/AuthContext';

const TIER_STYLES = {
  ben_premium: { accent: '#d4af37', icon: Crown, label: 'Best Value' },
  ben_standard: { accent: '#60A5FA', icon: Star, label: null },
  ben_base: { accent: '#22C993', icon: Shield, label: null },
  ben_new_adult: { accent: '#B794F6', icon: Award, label: 'Flat Rate' },
  ben_military: { accent: '#F59E0B', icon: Shield, label: 'Flat Rate' },
  ben_hospice: { accent: '#ec4899', icon: Heart, label: 'Post-Transition' },
  ben_veteran: { accent: '#059669', icon: Award, label: 'Flat Rate' },
  ben_seniors: { accent: '#FBBF24', icon: Sun, label: 'Flat Rate' },
  ben_enterprise: { accent: '#8B5CF6', icon: Zap, label: 'B2B Partner' },
  premium: { accent: '#d4af37', icon: Crown, label: 'Most Popular' },
  standard: { accent: '#60A5FA', icon: Star, label: null },
  base: { accent: '#22C993', icon: Shield, label: null },
  new_adult: { accent: '#B794F6', icon: Award, label: null },
  military: { accent: '#F59E0B', icon: Shield, label: null },
  hospice: { accent: '#ec4899', icon: Heart, label: 'Free' },
  veteran: { accent: '#059669', icon: Award, label: null },
  seniors: { accent: '#FBBF24', icon: Sun, label: null },
  enterprise: { accent: '#8B5CF6', icon: Zap, label: 'B2B Partner' },
};

// Beneficiary monthly price for a benefactor plan on the selected cycle — read from the
// ben_<tier> catalog entry so flat-rate beneficiary tiers show the same price on every cycle.
export const beneficiaryMonthlyPrice = (plan, beneficiaryPlans, billing) => {
  const bp = (beneficiaryPlans || []).find((p) => p.id === `ben_${plan.id}`);
  if (!bp) return plan.ben_price;
  if (billing === 'annual') return bp.annual_price ?? bp.price;
  if (billing === 'quarterly') return bp.quarterly_price ?? bp.price;
  return bp.price;
};

const BeneficiaryBillingToggle = ({ billing, onChange, plans }) => {
  const pct = (cycle) => { const v = maxCycleSaving(plans, cycle); return v > 0 ? `${v}%` : null; };
  const cycles = [
    { id: 'annual', label: 'Annual', save: pct('annual') },
    { id: 'quarterly', label: 'Quarterly', save: pct('quarterly') },
    { id: 'monthly', label: 'Monthly', save: null },
  ];

  return (
    <div className="mb-8 text-center" data-testid="billing-toggle">
      <div className="inline-flex p-1 rounded-2xl" style={{
        background: 'var(--s)',
        border: '1px solid var(--b)',
        boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.1)',
      }}>
        {cycles.map((c) => (
          <button
            key={c.id}
            onClick={() => onChange(c.id)}
            className="relative px-5 py-2.5 rounded-xl text-xs font-bold transition-all duration-300"
            style={{
              background: billing === c.id
                ? c.id === 'annual' ? 'linear-gradient(135deg, #22C993, #10b981)' : 'linear-gradient(135deg, #d4af37, #c9a033)'
                : 'transparent',
              color: billing === c.id ? '#0F1629' : 'var(--t5)',
              boxShadow: billing === c.id
                ? c.id === 'annual' ? '0 4px 16px rgba(34,201,147,0.35)' : '0 4px 16px rgba(var(--gold-rgb), 0.35)'
                : 'none',
            }}
            data-testid={`billing-${c.id}`}
          >
            {c.label}
            {c.save && (
              <span className="absolute -top-2 -right-1 text-[11px] font-bold px-1.5 py-0.5 rounded-full"
                style={{ background: '#22C993', color: '#fff', boxShadow: '0 2px 8px rgba(34,201,147,0.4)' }}>
                -{c.save}
              </span>
            )}
          </button>
        ))}
      </div>
      <p className="text-[11px] text-[var(--t5)] mt-2">Most families choose the annual plan to ensure uninterrupted access.</p>
    </div>
  );
};

const PriceDisplay = ({ plan, billing }) => {
  const basePrice = plan.price || 0;
  if (basePrice === 0) return <span className="text-3xl font-bold" style={{ fontFamily: 'var(--sans)' }}>Free</span>;

  let displayPrice = basePrice;
  if (billing === 'quarterly') displayPrice = plan.quarterly_price ?? basePrice;
  else if (billing === 'annual') displayPrice = plan.annual_price ?? basePrice;

  const annualTotal = (displayPrice * 12).toFixed(0);

  return (
    <div>
      <div className="flex items-baseline gap-1">
        <span className="text-3xl font-bold tracking-tight" style={{ fontFamily: 'var(--sans)' }}>
          ${displayPrice.toFixed(2)}
        </span>
        <span className="text-sm text-[var(--t4)]">/mo</span>
      </div>
      {billing === 'annual' && (
        <p className="text-xs text-[var(--gn2)] font-bold mt-0.5">${annualTotal}/year · Save {Math.round((1 - displayPrice / basePrice) * 100)}%</p>
      )}
      {billing === 'quarterly' && (
        <p className="text-xs text-[var(--t5)] mt-0.5">billed quarterly</p>
      )}
    </div>
  );
};

export const SubscriptionManagement = ({
  subscriptionStatus,
  refreshSubscription,
  getAuthHeaders,
  _onShowPaywall,
}) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  // Accounts tagged on an acquisition page (/benefactor) get the plan they saw there highlighted.
  const preferredPlan = user?.preferred_plan || 'premium';
  const [plans, setPlans] = useState([]);
  const [beneficiaryPlans, setBeneficiaryPlans] = useState([]);
  // Admin-configured per-tier feature gates (keyed by tier id).
  // Each value is an array of { label, enabled } in canonical order.
  // Source: GET /subscriptions/plans — built from subscription_settings.feature_gates.
  // When present, replaces plan.features marketing copy so the tiles
  // stay in lockstep with what's shown in the admin Feature Gates table.
  const [tierFeatures, setTierFeatures] = useState({});
  const [billing, setBilling] = useState('annual');
  const [subscribing, setSubscribing] = useState(null);
  const [cancellingPlan, setCancellingPlan] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [showFamilyRequest, setShowFamilyRequest] = useState(false);
  const [familyEmail, setFamilyEmail] = useState('');
  const [sendingRequest, setSendingRequest] = useState(false);
  // Verification state
  const [showVerification, setShowVerification] = useState(false);
  const [verificationTier, setVerificationTier] = useState('');
  const [verificationFile, setVerificationFile] = useState(null);
  const [verificationDocType, setVerificationDocType] = useState('');
  const [uploadingVerification, setUploadingVerification] = useState(false);
  const [verificationStatus, setVerificationStatus] = useState(null);
  const [changingBilling, setChangingBilling] = useState(false);

  const { purchaseWithIAP } = useIAPPurchase();

  // Portal-aware: show beneficiary plans only when in the beneficiary portal,
  // show benefactor plans when in the benefactor portal (even for multi-role users)
  const isInBeneficiaryPortal = window.location.pathname.startsWith('/beneficiary');
  const isBeneficiary = isInBeneficiaryPortal;
  const currentSub = subscriptionStatus?.subscription;
  // Only recognize a subscription as "active" if it matches the current portal context
  // (e.g., a ben_* plan shouldn't grey out benefactor plans, and vice versa)
  // ── AND only when the underlying status is genuinely active. The
  // legacy code path treated any row with a `plan_id` set as current,
  // so a `cancelled` or `past_due` row still painted "Current Plan"
  // — which made post-Stripe regressions feel like "still
  // unsubscribed" because no plan visibly matched what the user just
  // bought. (Root cause of the user's May-18 complaint.)
  const subIsActive = currentSub?.status === 'active' || currentSub?.status === 'past_due';
  const rawPlanId = subIsActive ? currentSub?.plan_id : null;
  const currentPlanId = isBeneficiary
    ? (rawPlanId?.startsWith('ben_') ? rawPlanId : null)
    : (rawPlanId && !rawPlanId.startsWith('ben_') ? rawPlanId : null);
  const currentBilling = subIsActive ? currentSub?.billing_cycle : null;
  // True when the active sub was set by a Founder/admin instead of a
  // real payment. We surface a small "Granted by Founder" badge on
  // the tile so a family inheriting a tier knows the grant origin
  // (and doesn't believe they're being silently charged).
  const subSource = currentSub?.source;
  const isAdminGranted = subIsActive && (subSource === 'admin_override' || subSource === 'estate_admin_tier');
  // Optimistic intent — only surfaces here when no active sub is in
  // play (server-side /status already enforces this). Used below to
  // paint the chosen tile with a gold "Processing payment…" ribbon
  // during the Stripe round-trip.
  const pendingIntent = !currentPlanId ? subscriptionStatus?.pending_intent : null;
  const pendingPlanId = pendingIntent?.plan_id || null;
  const _pendingBilling = pendingIntent?.billing_cycle || null;
  const isBeta = subscriptionStatus?.beta_mode;
  // When the founder turns on platform-wide Free Mode, the entire tier
  // selection is replaced by a single gold "Free Mode" banner and the
  // plan tiles are greyed out / non-interactive (no one is being charged).
  const platformFreeMode = subscriptionStatus?.platform_free_mode;
  const lockedTier = subscriptionStatus?.beneficiary_locked_tier;
  const estateTransitioned = subscriptionStatus?.estate_transitioned || false;
  const benCanSubscribe = !isBeneficiary || estateTransitioned;

  useEffect(() => {
    const fetchPlans = async () => {
      try {
        const res = await apiClient.get(`${API_URL}/subscriptions/plans`);
        setPlans(res.data.plans || []);
        setBeneficiaryPlans(res.data.beneficiary_plans || []);
        // Keep the tile checklist in sync with admin Feature Gates config.
        if (res.data.tier_features) setTierFeatures(res.data.tier_features);
      } catch (_e) { /* fallback empty */ }
    };
    const fetchVerification = async () => {
      try {
        const res = await apiClient.get(`${API_URL}/verification/status`, getAuthHeaders());
        setVerificationStatus(res.data);
      } catch (_e) { /* ignore */ }
    };
    fetchPlans();
    fetchVerification();
    // Only override billing default if user has an active subscription
    if (currentSub?.billing_cycle) setBilling(currentSub.billing_cycle);
  }, [currentBilling]); // eslint-disable-line react-hooks/exhaustive-deps

  // For beneficiaries: show only their locked-in tier (determined by benefactor's majority plan)
  // If no locked tier yet, show nothing — they can't choose
  // For under-18 beneficiaries: no charge tier at all
  const isMinorBeneficiary = isBeneficiary && subscriptionStatus?.is_minor;
  const displayPlans = isBeneficiary
    ? (isMinorBeneficiary ? [] : (lockedTier ? beneficiaryPlans.filter(p => p.id === lockedTier) : []))
    : plans;

  // ── Lazy-collapse layout ────────────────────────────────────────────
  // Main paywall shows the three universal tiers prominently. The
  // discount/eligibility tiers (New Adult, Military, Veteran, Hospice,
  // Enterprise) are tucked behind a gold pill so the page feels
  // focused on the 95% case. Beneficiaries always see only their
  // locked tier — no pill needed.
  // Discount tiers = plans that require eligibility (founder-defined); order follows the founder's plan_order.
  const isMainTier = (id) => !isDiscountTier(displayPlans.find(p => p.id === id));
  const mainPlans = isBeneficiary ? displayPlans : displayPlans.filter(p => !isDiscountTier(p));
  const discountPlans = isBeneficiary ? [] : displayPlans.filter(isDiscountTier);
  const [discountOpen, setDiscountOpen] = useState(false);
  const discountSectionRef = useRef(null);

  // Determine auto-selected tier from eligible_tiers / special_status
  const eligibleTiers = subscriptionStatus?.eligible_tiers || [];
  const specialStatus = subscriptionStatus?.special_status || [];
  const hasSpecialStatus = specialStatus.length > 0;
  const ageTier = eligibleTiers.find(t => displayPlans.some(p => p.id === t)) || null;
  const autoTier = hasSpecialStatus
    ? (specialStatus.includes('hospice') ? 'hospice' : specialStatus.includes('veteran') ? 'veteran' : specialStatus.includes('seniors') ? 'seniors' : specialStatus.includes('enterprise') ? 'enterprise' : 'military')
    : ageTier;
  const autoPlan = displayPlans.find(p => p.id === autoTier);

  // Auto-open the discount section when the user has a verified
  // discount tier (military / veteran / hospice / enterprise / new
  // adult). This keeps a verified founder from staring at a hidden
  // tier they qualify for — the pill is pre-expanded for them.
  useEffect(() => {
    if (autoTier && !isMainTier(autoTier)) setDiscountOpen(true);
  }, [autoTier]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleVerifyB2bCode = async () => {
    if (!b2bCode.trim()) { toast.error('Please enter your partner code'); return; }
    setVerifyingCode(true);
    try {
      const res = await apiClient.post(`${API_URL}/subscriptions/verify-b2b-code`, { code: b2bCode }, getAuthHeaders());
      if (res.data.verified) {
        refreshSubscription?.();
        setB2bCode('');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Invalid code');
    } finally {
      setVerifyingCode(false);
    }
  };

  // Should a plan be greyed out (not selectable)?
  //
  // Hard locks live in the beneficiary `lockedTier` path (handled in
  // the `currentSub`/beneficiary grid elsewhere). `autoTier` is a
  // SUGGESTION — it highlights "Your Tier" + the verification banner —
  // but never restricts the user from choosing Premium/Standard/Base
  // at full price if they want to. Returning true here would apply
  // `pointer-events-none` to the card and silently break the Subscribe
  // button for every eligibility-flagged user (regression fixed
  // Feb 18, 2026 — was blocking Veterans / Military / Seniors / New
  // Adults from subscribing to any main tier).
  const isPlanLocked = (_planId) => false;

  // Check if beneficiary's locked plan allows billing toggle (military does not)
  const lockedPlan = lockedTier ? beneficiaryPlans.find(p => p.id === lockedTier) : null;
  const showBillingToggle = !isBeneficiary || (lockedPlan && lockedPlan.allows_billing_toggle !== false);
  const beneficiaryNoTierYet = isBeneficiary && !lockedTier;

  const planById = (planId) => displayPlans.find(p => p.id === planId);
  const requiresVerification = (planId) => verifiesBeforeCheckout(planById(planId));

  // Check if user is already verified for a tier
  const isVerifiedFor = (planId) => {
    return subscriptionStatus?.verification?.status === 'approved' &&
      subscriptionStatus?.verification?.tier_requested === planId;
  };

  // B2B code verification state
  const [b2bCode, setB2bCode] = useState('');
  const [verifyingCode, setVerifyingCode] = useState(false);

  // Detect if a plan/billing change is a downgrade (would require refund)
  const PLAN_RANK = { base: 1, new_adult: 2, veteran: 3, military: 3, enterprise: 3, seniors: 4, standard: 4, premium: 5, hospice: 0 };
  const CYCLE_RANK = { monthly: 1, quarterly: 2, annual: 3 };

  const isDowngrade = (newPlanId, newCycle) => {
    if (!currentSub) return false;
    const oldPlanRank = PLAN_RANK[currentPlanId] || 0;
    const newPlanRank = PLAN_RANK[newPlanId] || 0;
    const oldCycleRank = CYCLE_RANK[currentBilling] || 1;
    const newCycleRank = CYCLE_RANK[newCycle] || 1;
    // Downgrade if moving to a lower tier, OR same tier but shorter billing cycle
    return newPlanRank < oldPlanRank || (newPlanRank === oldPlanRank && newCycleRank < oldCycleRank);
  };

  const handleVerificationUpload = async () => {
    if (!verificationFile || !verificationDocType) {
      toast.error('Please select a document type and upload a file');
      return;
    }
    // Suspend auto-logout for the ENTIRE upload window — see
    // matching comment in SubscriptionPaywall.handleVerificationUpload.
    const releaseAutoLogout = suspendAutoLogout();
    setUploadingVerification(true);
    try {
      const base64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (e) => {
          try {
            resolve(e.target.result.split(',')[1]);
          } catch (parseErr) {
            reject(parseErr);
          }
        };
        reader.onerror = () => reject(new Error('Could not read the selected file'));
        reader.readAsDataURL(verificationFile);
      });
      const formData = new FormData();
      formData.append('tier_requested', verificationTier);
      formData.append('doc_type', verificationDocType);
      formData.append('file_data', base64);
      formData.append('file_name', verificationFile.name);
      await apiClient.post(`${API_URL}/verification/upload`, formData, {
        ...getAuthHeaders(),
        timeout: 90000,
      });
      setShowVerification(false);
      setVerificationFile(null);
      setVerificationDocType('');
      setVerificationStatus({ status: 'pending', tier_requested: verificationTier });
    } catch (err) {
      const msg = err?.response?.data?.detail
        || (err?.code === 'ECONNABORTED' ? 'Upload timed out. Please try a smaller file or check your connection.' : null)
        || err?.message
        || 'Verification upload failed';
      toast.error(msg);
    } finally {
      setUploadingVerification(false);
      releaseAutoLogout();
    }
  };

  const handleSubscribe = async (planId) => {
    // Gate verification-required plans
    if (requiresVerification(planId) && !isVerifiedFor(planId)) {
      if (verificationStatus?.status === 'pending') {
        return;
      }
      setVerificationTier(planId);
      setShowVerification(true);
      return;
    }

    setSubscribing(planId);
    try {
      // Native iOS app → Apple In-App Purchase (MUST use IAP — Apple Guideline 3.1.1)
      if (isNative) {
        const iapResult = await purchaseWithIAP(planId, billing);
        if (iapResult.cancelled) {
          setSubscribing(null);
          return;
        }
        if (refreshSubscription) await refreshSubscription();
        setSubscribing(null);
        return;
      }

      // Web/PWA → Stripe
      const res = await apiClient.post(`${API_URL}/subscriptions/checkout`, {
        plan_id: planId,
        billing_cycle: billing,
        origin_url: window.location.origin,
      }, getAuthHeaders());
      if (res.data.url) {
        // In a standalone PWA / dock app this opens Stripe in a
        // separate browser window so the in-app session is preserved
        // when the user comes back; in a normal browser tab it falls
        // back to the legacy in-window redirect.
        openStripeCheckout(res.data.url);
      } else if (res.data.free) {
        if (refreshSubscription) await refreshSubscription();
      }
    } catch (e) {
      const detail = e.response?.data?.detail || e.message || 'Failed to start checkout';
      if (!detail.includes('beta')) {
        toast.error(detail);
      }
    }
    setSubscribing(null);
  };

  const handleChangePlan = async (planId) => {
    if (planId === currentPlanId && billing === currentBilling) return;

    // Downgrade → route to the in-app Customer Service chat. The user
    // can articulate their request there with full context (and we
    // get a richer thread than a one-shot system message).
    if (isDowngrade(planId, billing)) {
      navigate('/support');
      return;
    }

    // Native iOS → Apple IAP for upgrades (Apple Guideline 3.1.1)
    if (isNative) {
      setSubscribing(planId);
      try {
        const iapResult = await purchaseWithIAP(planId, billing);
        if (iapResult.cancelled) {
          setSubscribing(null);
          return;
        }
        if (refreshSubscription) await refreshSubscription();
      } catch (e) {
        toast.error(e.message || 'Failed to change plan');
      }
      setSubscribing(null);
      return;
    }

    // Web → Stripe upgrade
    setSubscribing(planId);
    try {
      const res = await apiClient.post(`${API_URL}/subscriptions/change-plan`, {
        plan_id: planId,
        billing_cycle: billing,
        origin_url: window.location.origin,
      }, getAuthHeaders());
      if (res.data.url) {
        openStripeCheckout(res.data.url);
      } else if (res.data.success) {
        // toast removed
        if (refreshSubscription) await refreshSubscription();
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to change plan');
    }
    setSubscribing(null);
  };

  const handleChangeBilling = async () => {
    // Downgrading billing cycle (e.g., annual → quarterly) → route to
    // the Customer Service chat so the user can request the change.
    if (isDowngrade(currentPlanId, billing)) {
      navigate('/support');
      return;
    }

    // Native iOS → Apple IAP for billing upgrades (Apple Guideline 3.1.1)
    if (isNative) {
      setChangingBilling(true);
      try {
        const iapResult = await purchaseWithIAP(currentPlanId, billing);
        if (iapResult.cancelled) {
          setChangingBilling(false);
          return;
        }
        if (refreshSubscription) await refreshSubscription();
      } catch (e) {
        toast.error(e.message || 'Failed to change billing cycle');
      }
      setChangingBilling(false);
      return;
    }

    // Web → Stripe upgrade billing cycle
    setChangingBilling(true);
    try {
      const res = await apiClient.post(`${API_URL}/subscriptions/change-billing`, {
        billing_cycle: billing,
        origin_url: window.location.origin,
      }, getAuthHeaders());
      if (res.data.url) {
        openStripeCheckout(res.data.url);
      } else if (res.data.success) {
        // toast removed
        if (refreshSubscription) await refreshSubscription();
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to change billing cycle');
    }
    setChangingBilling(false);
  };

  const handleCancelSubscription = async () => {
    setCancellingPlan(true);
    try {
      await apiClient.post(`${API_URL}/subscriptions/cancel`, {}, getAuthHeaders());
      // toast removed
      setShowCancelConfirm(false);
      if (refreshSubscription) await refreshSubscription();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to cancel');
    }
    setCancellingPlan(false);
  };

  const handleFamilyPlanRequest = async () => {
    if (!familyEmail.trim()) {
      toast.error('Please enter the benefactor\'s email');
      return;
    }
    setSendingRequest(true);
    try {
      await apiClient.post(`${API_URL}/subscriptions/family-plan-request`, {
        benefactor_email: familyEmail.trim(),
      }, getAuthHeaders());
      // toast removed
      setShowFamilyRequest(false);
      setFamilyEmail('');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to send request');
    }
    setSendingRequest(false);
  };

  return (
    <Card className="glass-card overflow-hidden" data-testid="subscription-management">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-[var(--t)] flex items-center gap-2.5" style={{ fontFamily: 'var(--sans)' }}>
            <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: 'linear-gradient(135deg, rgba(var(--gold-rgb), 0.15), rgba(var(--gold-rgb), 0.05))', border: '1px solid rgba(var(--gold-rgb), 0.2)' }}>
              <CreditCard className="w-4.5 h-4.5 text-[var(--gold)]" />
            </div>
            {isBeneficiary ? 'Your Plan' : 'Subscription'}
          </CardTitle>
          {currentSub?.status === 'active' && (
            <button onClick={() => setShowCancelConfirm(true)} className="text-[11px] text-[var(--t5)] hover:text-red-400 transition-colors px-3 py-1 rounded-lg hover:bg-red-500/5" data-testid="cancel-sub-btn">
              Cancel Plan
            </button>
          )}
        </div>
      </CardHeader>
      <CardContent className="pt-2">
        {/* Free Mode — replaces the choose/your-plan flow entirely. */}
        {platformFreeMode && <FreeModeBanner className="mb-6" />}
        {/* Status Banner — shown ONLY for non-active states (beta,
            free trial, or no-plan-chosen). Active subscribers already
            see their full plan name + price in the "Current Plan"
            card below, so an extra "${plan_name} · ${cycle}" banner
            here is redundant — and was rendering as the confusing
            "undefined · annual" when plan_name wasn't populated. */}
        {subscriptionStatus && !platformFreeMode && !(currentSub?.status === 'active' && !isBeta) && (
          <div className="mb-6 p-4 rounded-xl relative overflow-hidden" style={{
            background: isBeta
              ? 'linear-gradient(135deg, rgba(139,92,246,0.08), rgba(59,130,246,0.05))'
              : 'linear-gradient(135deg, rgba(var(--gold-rgb), 0.08), rgba(var(--gold-rgb), 0.02))',
            border: `1px solid ${isBeta ? 'rgba(139,92,246,0.15)' : 'rgba(var(--gold-rgb), 0.15)'}`,
          }}>
            <div className="flex items-center gap-2.5">
              {isBeta ? (
                <Sparkles className="w-4 h-4 text-purple-400" />
              ) : (
                <Clock className="w-4 h-4 text-[var(--gold)]" />
              )}
              <div>
                <span className="text-sm font-semibold text-[var(--t)]">
                  {isBeta ? 'Beta Access — All features unlocked' :
                   subscriptionStatus.trial?.trial_active
                      ? `Exploration Period · ${subscriptionStatus.trial.days_remaining} days left`
                      : 'Choose a payment option that best suits you. (Monthly/Quarterly/Annual)'}
                </span>
                {isBeta && (
                  <p className="text-[11px] text-[var(--t5)] mt-0.5">No payment required during beta period</p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Cancel Confirm */}
        {showCancelConfirm && (
          <div className="mb-5 p-4 rounded-xl" style={{ background: 'rgba(239,68,68,0.05)', border: '1px solid rgba(239,68,68,0.15)' }}>
            <p className="text-sm text-[var(--t3)] mb-3">Cancel your subscription? Access continues until the end of your billing period.</p>
            <div className="flex gap-2">
              <Button onClick={handleCancelSubscription} disabled={cancellingPlan}
                className="text-xs px-4 py-2" style={{ background: 'rgba(239,68,68,0.1)', color: '#ef4444', border: '1px solid rgba(239,68,68,0.2)' }}
                data-testid="confirm-cancel-btn">
                {cancellingPlan ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : null} Yes, Cancel
              </Button>
              <Button onClick={() => setShowCancelConfirm(false)} className="text-xs px-4 py-2 bg-[var(--s)] text-[var(--t4)] border border-[var(--b)]">Keep Plan</Button>
            </div>
          </div>
        )}

        {/* Billing Toggle — hidden for military beneficiaries (flat rate).
            Greyed + disabled under Free Mode (no plan to bill). */}
        {showBillingToggle && (
          <div className={platformFreeMode ? 'opacity-40 grayscale pointer-events-none select-none' : ''} aria-hidden={platformFreeMode ? 'true' : undefined}>
            <BeneficiaryBillingToggle billing={billing} onChange={setBilling} plans={displayPlans} />
          </div>
        )}

        {/* Locked tier info for beneficiaries */}
        {isBeneficiary && lockedPlan && (
          <div className="mb-5 p-4 rounded-xl" style={{ background: 'rgba(37,99,235,0.04)', border: '1px solid rgba(37,99,235,0.1)' }}>
            <p className="text-xs text-[var(--t4)] leading-relaxed">
              Your tier was set by your benefactor based on the plan they held for the majority of their subscription period.
              {!lockedPlan.allows_billing_toggle && ' This plan has flat-rate pricing with no quarterly or annual discounts.'}
            </p>
          </div>
        )}

        {/* Founders Circle beneficiary message */}
        {isBeneficiary && subscriptionStatus?.free_access && !platformFreeMode && (
          <div className="mb-5 p-4 rounded-xl flex items-start gap-3" style={{ background: 'rgba(var(--gold-rgb), 0.08)', border: '1px solid rgba(var(--gold-rgb), 0.25)' }}>
            <Crown className="w-5 h-5 text-[var(--gold)] flex-shrink-0 mt-0.5" />
            <p className="text-xs text-[var(--t3)] leading-relaxed">
              Your benefactor was gracious and forward-thinking enough to become a <span className="font-bold text-[var(--gold)]">Founders Circle</span> member, securing your lifetime access to CarryOn at no cost to you — ever. This applies to all current and future beneficiaries of their estate.
            </p>
          </div>
        )}

        {/* Beneficiary: no tier determined yet */}
        {beneficiaryNoTierYet && !isMinorBeneficiary && (
          <div className="p-6 rounded-2xl text-center" style={{ background: 'var(--s)', border: '1px solid var(--b)' }}>
            <CreditCard className="w-10 h-10 mx-auto text-[var(--gold)] mb-3 opacity-50" />
            <h4 className="text-sm font-bold text-[var(--t)] mb-2" style={{ fontFamily: 'var(--sans)' }}>Your Plan Is Determined by Your Benefactor</h4>
            <p className="text-xs text-[var(--t4)] leading-relaxed max-w-md mx-auto mb-4">
              Your beneficiary tier is automatically set based on the plan your benefactor held for the majority of their subscription period with CarryOn. You do not need to select a plan — it will appear here once determined.
            </p>
            <p className="text-[11px] text-[var(--t5)]">
              Beneficiary pricing does not begin until a verified transition event occurs. Minor beneficiaries (under 18) are free.
            </p>
          </div>
        )}

        {/* Minor beneficiary: no charge */}
        {isMinorBeneficiary && (
          <div className="p-6 rounded-2xl text-center" style={{ background: 'var(--s)', border: '1px solid var(--b)' }}>
            <Users className="w-10 h-10 mx-auto text-[#22C993] mb-3 opacity-50" />
            <h4 className="text-sm font-bold text-[var(--t)] mb-2" style={{ fontFamily: 'var(--sans)' }}>No Subscription Required</h4>
            <p className="text-xs text-[var(--t4)] leading-relaxed max-w-md mx-auto">
              Beneficiaries under 18 have full access at no charge. Your access is managed through your benefactor's estate plan.
            </p>
          </div>
        )}

        {/* Auto-tier info banner — dimmed/desaturated under Free Mode so it
            doesn't stand out in orange while every plan below is paused. */}
        {autoTier && !isBeneficiary && (
          <div
            className={`mb-4 p-3 rounded-xl${platformFreeMode ? ' opacity-40 grayscale pointer-events-none select-none transition-opacity duration-300' : ''}`}
            style={{ background: 'rgba(245,158,11,0.06)', border: '1px solid rgba(245,158,11,0.15)' }}
            aria-hidden={platformFreeMode ? 'true' : undefined}
          >
            <p className="text-xs text-[var(--yw)] leading-relaxed">
              {autoTier === 'enterprise'
                ? `You selected ${autoPlan?.name || 'Enterprise / B2B Partner'}. Enter your partner code below to activate your access.`
                : hasAgeWindow(autoPlan)
                ? `Based on your age (${ageLabel(autoPlan)}), you qualify for the ${autoPlan.name} tier. A government-issued ID is required after subscribing.`
                : `Based on your special eligibility, you qualify for the ${autoPlan?.name || autoTier} tier. Verification is required after subscribing.`}
            </p>
          </div>
        )}

        {/* Plan Cards — main 3 tiers prominently, then a gold pill that
            slides down the eligibility/discount tiers (New Adult,
            Military, Veteran, Hospice, Enterprise) when tapped. The
            inline `renderPlanCard` is reused by both grids so the card
            markup stays single-source-of-truth and zero-regression.
            When Free Mode is on, the whole region is greyed out and
            made non-interactive — the gold banner above takes over. */}
        <div
          className={platformFreeMode ? 'opacity-40 grayscale pointer-events-none select-none transition-opacity duration-300' : ''}
          aria-hidden={platformFreeMode ? 'true' : undefined}
          data-testid="plan-selection-region"
        >
        {(() => {
          const renderPlanCard = (plan, useFlexWidth) => {
            const style = TIER_STYLES[plan.id] || TIER_STYLES.base;
            const Icon = style.icon;
            const isCurrent = currentPlanId === plan.id;
            // Optimistic-payment tile state. Painted when there is no
            // active sub yet AND this plan matches the just-clicked
            // checkout intent. Flips back to `isCurrent` automatically
            // when the webhook activates (intent is then deleted).
            const isPendingThisPlan = !!(pendingPlanId && pendingPlanId === plan.id);
            const isRecommended = plan.id === 'ben_premium' || plan.id === preferredPlan;
            const locked = isPlanLocked(plan.id);
            const isAutoSelected = autoTier === plan.id;
            // Flex-wrap rows need explicit per-card widths so any orphan
            // row centers cleanly (gap-4 → 0.5rem / 0.667rem fudge).
            const flexWidth = useFlexWidth
              ? 'w-full sm:w-[calc(50%-0.5rem)] lg:w-[calc(33.333%-0.667rem)]'
              : '';

            // Premium Annual recommendation pulse
            const isPremiumAnnual = (plan.id === 'premium' || plan.id === 'ben_premium') && billing === 'annual';
            const showRecommendedPulse = isPremiumAnnual && currentPlanId && !isCurrent && !(currentPlanId === plan.id && currentBilling === 'annual');

            return (
              <div key={plan.id} className={`${flexWidth} relative rounded-2xl overflow-hidden transition-all duration-300 group ${locked ? 'opacity-40 pointer-events-none' : isCurrent ? '' : 'hover:-translate-y-1'}`} style={{
                // Tile surface — every state gets a subtle accent wash so
                // light mode doesn't fall back to a flat near-white that
                // makes the row look monochromatic. Alpha is intentionally
                // low (~10%) so dark-mode contrast stays correct AND light
                // mode picks up just enough color to differentiate tiers.
                background: isAutoSelected
                  ? `linear-gradient(168deg, ${style.accent}22, ${style.accent}0a)`
                  : isCurrent || isPendingThisPlan
                  ? `linear-gradient(168deg, ${style.accent}1c, ${style.accent}06)`
                  : `linear-gradient(168deg, ${style.accent}14, var(--s) 75%)`,
                border: isAutoSelected
                  ? `2px solid ${style.accent}`
                  : isCurrent
                  ? `2px solid ${style.accent}`
                  : isPendingThisPlan
                    ? `2px dashed ${style.accent}`
                    : isRecommended
                      ? `2px solid ${style.accent}50`
                      : `1px solid ${style.accent}30`,
                boxShadow: isAutoSelected
                  ? `0 8px 32px ${style.accent}25`
                  : isCurrent || isPendingThisPlan
                  ? `0 8px 32px ${style.accent}20`
                  : isRecommended
                    ? `0 8px 32px ${style.accent}15`
                    : '0 2px 8px rgba(0,0,0,0.05)',
                opacity: (currentPlanId || pendingPlanId) && !isCurrent && !isPendingThisPlan && !showRecommendedPulse ? 0.5 : 1,
                animation: isPendingThisPlan
                  ? 'pendingPulseMgmt 1.6s ease-in-out infinite'
                  : showRecommendedPulse
                    ? 'recommendedPulseMgmt 2.5s ease-in-out infinite'
                    : 'none',
              }} data-testid={`plan-${plan.id}`}>
                {/* Recommended pulse animation */}
                {showRecommendedPulse && (
                  <style>{`
                    @keyframes recommendedPulseMgmt {
                      0%, 100% { box-shadow: 0 8px 32px ${style.accent}20; opacity: 1; }
                      50% { box-shadow: 0 8px 32px ${style.accent}40, 0 0 0 3px ${style.accent}15; opacity: 1; }
                    }
                  `}</style>
                )}
                {isPendingThisPlan && (
                  <style>{`
                    @keyframes pendingPulseMgmt {
                      0%, 100% { box-shadow: 0 8px 32px ${style.accent}33; }
                      50%      { box-shadow: 0 8px 32px ${style.accent}66, 0 0 0 3px ${style.accent}22; }
                    }
                  `}</style>
                )}

                {/* Label badges */}
                {(style.label || verificationBadge(plan) || isCurrent || isAutoSelected || isPendingThisPlan || showRecommendedPulse) && (
                  <div className="absolute top-0 left-1/2 -translate-x-1/2 text-[11px] font-bold px-3 py-0.5 rounded-b-lg z-10 flex items-center gap-1"
                    style={{
                      background: isPendingThisPlan
                        ? 'linear-gradient(180deg, #d4af37, #b8962e)'
                        : showRecommendedPulse
                          ? '#22C993'
                          : style.accent,
                      color: showRecommendedPulse ? '#fff' : 'var(--bg2)',
                    }}>
                    {isPendingThisPlan ? (
                      <>
                        <Loader2 className="w-3 h-3 animate-spin" />
                        Processing Payment…
                      </>
                    ) : isCurrent ? 'Current Plan' : isAutoSelected ? 'Your Tier' : showRecommendedPulse ? 'Recommended — Best Value' : (style.label || verificationBadge(plan))}
                  </div>
                )}
                {/* Founder-granted attribution — only on the active
                    tile, only when source proves admin origin. Sits
                    flush under the Current Plan badge so the user can
                    see "this is paid for by CarryOn, not by me." */}
                {isCurrent && isAdminGranted && (
                  <div
                    className="absolute top-5 left-1/2 -translate-x-1/2 text-[11px] font-medium px-2 py-0.5 rounded-b-md whitespace-nowrap z-10 flex items-center gap-1"
                    style={{
                      color: '#22C993',
                      background: 'rgba(34, 201, 147, 0.10)',
                      border: '1px solid rgba(34, 201, 147, 0.30)',
                      borderTop: 'none',
                    }}
                    title="This tier was granted to your account by CarryOn — you are not being charged."
                    data-testid={`plan-${plan.id}-granted-by-founder`}
                  >
                    <Sparkles className="w-3 h-3" />
                    Granted by Founder
                  </div>
                )}
                {/* ETA hint — sits flush under the gold ribbon so the
                    user knows we're seconds from settling, not minutes.
                    Suppresses tab-away churn on a slow webhook day. */}
                {isPendingThisPlan && (
                  <div
                    className="absolute top-5 left-1/2 -translate-x-1/2 text-[11px] font-medium px-2 py-0.5 rounded-b-md whitespace-nowrap z-10"
                    style={{
                      color: 'rgba(var(--gold-rgb), 0.95)',
                      background: 'rgba(var(--gold-rgb), 0.10)',
                      border: '1px solid rgba(var(--gold-rgb), 0.25)',
                      borderTop: 'none',
                    }}
                    data-testid={`plan-${plan.id}-pending-eta`}
                  >
                    usually ≤ 5 seconds
                  </div>
                )}

                <div className="p-5 pt-7 flex flex-col h-full">
                  {/* Plan header */}
                  <div className="flex items-center gap-2 mb-4">
                    <div className="w-9 h-9 rounded-xl flex items-center justify-center transition-transform duration-300 group-hover:scale-110"
                      style={{ background: `${style.accent}12`, border: `1px solid ${style.accent}25` }}>
                      <Icon className="w-4 h-4" style={{ color: style.accent }} />
                    </div>
                    <h3 className="font-bold text-base text-[var(--t)]" style={{ fontFamily: 'var(--sans)' }}>{plan.name}</h3>
                  </div>

                  {/* Price — animates on billing change */}
                  <div className="mb-4" style={{ color: style.accent }}>
                    <PriceDisplay plan={plan} billing={billing} />
                  </div>

                  {/* Beneficiary price — only on benefactor side */}
                  {!isBeneficiary && plan.ben_price !== undefined && (
                    <div className="mb-4 -mt-2 text-[var(--t4)] text-sm">
                      Beneficiary: <span className="font-bold text-[var(--t3)]">${Number(beneficiaryMonthlyPrice(plan, beneficiaryPlans, billing)).toFixed(2)}/mo</span>
                    </div>
                  )}

                  {/* Divider */}
                  <div className="h-px mb-4" style={{ background: `linear-gradient(90deg, transparent, ${style.accent}20, transparent)` }} />

                  {/* Features — prefer admin-configured tier_features (single
                      source of truth with Feature Gates config); fall back to
                      the plan's legacy marketing copy only if the gate grid
                      is missing. We intentionally DO NOT render disabled
                      items here (even with a strikethrough) — negative
                      advertising hurts conversion. Each tile shows only
                      what's included at that tier. */}
                  <div className="space-y-2 mb-5 flex-1">
                    {(tierFeatures[plan.id] && tierFeatures[plan.id].length > 0
                      ? tierFeatures[plan.id]
                      : (plan.features || []).map(f => typeof f === 'string' ? { label: f, enabled: true } : f)
                    ).filter(f => (typeof f === 'string' ? true : f.enabled !== false))
                      .map((f, i) => {
                        const label = typeof f === 'string' ? f : f.label;
                        return (
                          <div key={i} className="flex items-start gap-2 text-sm text-[var(--t3)]">
                            <div className="w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5"
                              style={{ background: `${style.accent}12` }}>
                              <Check className="w-2.5 h-2.5" style={{ color: style.accent }} />
                            </div>
                            <span>{label}</span>
                          </div>
                        );
                      })}
                  </div>

                  {plan.note && <p className="text-sm text-[var(--t4)] italic mb-3">{plan.note}</p>}

                  {/* CTA Button */}
                  {isCurrent ? (
                    <div className="space-y-2">
                      <div className="w-full text-center text-xs font-bold py-3 rounded-xl"
                        style={{ background: `${style.accent}10`, color: style.accent, border: `1px solid ${style.accent}25` }}>
                        <Check className="w-3 h-3 inline mr-1" /> Active · {currentBilling || billing}
                      </div>
                      {billing !== currentBilling && (
                        <Button
                          onClick={() => handleChangeBilling()}
                          disabled={changingBilling}
                          className="w-full text-xs font-bold py-3 transition-all duration-300"
                          style={isDowngrade(currentPlanId, billing)
                            ? { background: 'transparent', color: '#60A5FA', border: '1px solid rgba(96,165,250,0.3)' }
                            : { background: `linear-gradient(135deg, ${style.accent}, ${style.accent}cc)`, color: 'var(--bg2)', boxShadow: `0 4px 16px ${style.accent}30` }
                          }
                          data-testid="change-billing-btn"
                        >
                          {changingBilling ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> :
                           isDowngrade(currentPlanId, billing) ? <Mail className="w-3 h-3 mr-1" /> : <ArrowRight className="w-3 h-3 mr-1" />}
                          {isDowngrade(currentPlanId, billing) ? `Contact Support` : `Switch to ${billing}`}
                        </Button>
                      )}
                    </div>
                  ) : requiresVerification(plan.id) && !isVerifiedFor(plan.id) ? (
                    // Verification required — show status, code input for enterprise, or verify button
                    plan.id === 'enterprise' ? (
                      <div className="space-y-2">
                        <div className="flex gap-2">
                          <Input
                            value={b2bCode}
                            onChange={(e) => setB2bCode(e.target.value.toUpperCase())}
                            placeholder="Enter partner code"
                            className="flex-1 text-sm"
                            style={{ background: 'var(--bg)', border: '1px solid var(--b)' }}
                            data-testid="b2b-code-input"
                          />
                          <Button
                            onClick={handleVerifyB2bCode}
                            disabled={verifyingCode || !b2bCode.trim()}
                            className="text-xs font-bold px-4"
                            style={{ background: `linear-gradient(135deg, ${style.accent}, ${style.accent}cc)`, color: '#fff' }}
                            data-testid="verify-b2b-code-btn"
                          >
                            {verifyingCode ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Activate'}
                          </Button>
                        </div>
                        <p className="text-[11px] text-[var(--t5)] text-center">Code provided by your employer or partner</p>
                      </div>
                    ) : verificationStatus?.status === 'pending' && verificationStatus?.tier_requested === plan.id ? (
                      <div className="w-full text-center text-xs font-bold py-3 rounded-xl"
                        style={{ background: `${style.accent}08`, color: style.accent, border: `1px dashed ${style.accent}40` }}>
                        <Clock className="w-3 h-3 inline mr-1" /> Verification Pending
                      </div>
                    ) : (
                      <Button
                        onClick={() => handleSubscribe(plan.id)}
                        disabled={subscribing === plan.id}
                        className="w-full text-xs font-bold py-4 transition-all duration-300"
                        style={{ background: 'transparent', color: style.accent, border: `2px solid ${style.accent}35` }}
                        data-testid={`subscribe-${plan.id}`}
                      >
                        <Shield className="w-3.5 h-3.5 mr-1" /> Verify & Subscribe
                      </Button>
                    )
                  ) : currentSub?.status === 'active' ? (
                    <Button
                      onClick={() => handleChangePlan(plan.id)}
                      disabled={subscribing === plan.id}
                      className="w-full text-xs font-bold py-4 transition-all duration-300"
                      style={isDowngrade(plan.id, billing)
                        ? { background: 'transparent', color: '#60A5FA', border: '1px solid rgba(96,165,250,0.3)' }
                        : { background: `linear-gradient(135deg, ${style.accent}, ${style.accent}cc)`, color: 'var(--bg2)', boxShadow: `0 4px 16px ${style.accent}30` }
                      }
                      data-testid={`change-plan-${plan.id}`}
                    >
                      {subscribing === plan.id ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> :
                       isDowngrade(plan.id, billing) ? <Mail className="w-3.5 h-3.5 mr-1" /> : <ArrowRight className="w-3.5 h-3.5 mr-1" />}
                      {isDowngrade(plan.id, billing) ? 'Contact Support' : 'Upgrade'}
                    </Button>
                  ) : (
                    !benCanSubscribe ? (
                      <div className="w-full text-center text-xs font-bold py-3 rounded-xl opacity-50 cursor-not-allowed"
                        style={{ background: 'var(--s)', color: 'var(--t5)', border: '1px solid var(--b)' }}
                        data-testid={`subscribe-${plan.id}-disabled`}
                      >
                        Available after transition
                      </div>
                    ) : (
                    <Button
                      onClick={() => handleSubscribe(plan.id)}
                      disabled={subscribing === plan.id}
                      className="w-full text-xs font-bold py-4 transition-all duration-300"
                      style={{
                        background: isRecommended
                          ? `linear-gradient(135deg, ${style.accent}, ${style.accent}cc)`
                          : 'transparent',
                        color: isRecommended ? '#0F1629' : style.accent,
                        border: isRecommended ? 'none' : `2px solid ${style.accent}35`,
                        boxShadow: isRecommended ? `0 4px 20px ${style.accent}30` : 'none',
                      }}
                      data-testid={`subscribe-${plan.id}`}
                    >
                      {subscribing === plan.id ? (
                        <><Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> Processing...</>
                      ) : (
                        <>Subscribe <ChevronRight className="w-3.5 h-3.5 ml-1" /></>
                      )}
                    </Button>
                    )
                  )}
                </div>
              </div>
            );
          };

          return (
            <>
              {/* Main 3 tiers (Premium / Standard / Base) — symmetric
                  3-up grid. Always visible. For beneficiaries this is
                  also the only grid (their one locked tier renders
                  here). */}
              <div className={`gap-4 ${mainPlans.length === 1 ? 'grid grid-cols-1 max-w-sm mx-auto' : 'grid grid-cols-1 sm:grid-cols-3'}`} data-testid="plan-grid">
                {mainPlans.map(p => renderPlanCard(p, false))}
              </div>

              {/* Eligibility pill + collapsible discount-tier grid. */}
              {discountPlans.length > 0 && (
                <div className="mt-8" data-testid="paywall-discount-section">
                  <div className="flex justify-center px-2">
                    <button
                      type="button"
                      onClick={() => setDiscountOpen(o => !o)}
                      aria-expanded={discountOpen}
                      aria-controls="paywall-discount-tiers"
                      data-testid="paywall-eligibility-button"
                      className="rounded-full px-5 py-3 sm:px-6 sm:py-3.5 text-center max-w-3xl transition-transform hover:-translate-y-0.5 active:translate-y-0 cursor-pointer"
                      style={{
                        background: 'var(--gold)',
                        border: '2px solid #b89220',
                        boxShadow: '0 0 48px -16px rgba(var(--gold-rgb), 0.45)',
                      }}
                    >
                      <span
                        className="font-semibold leading-snug inline-flex items-center justify-center gap-2"
                        style={{ color: '#0b1120', fontSize: 'clamp(13px, 1.2vw, 15px)' }}
                      >
                        {discountTiersBlurb(discountPlans).replace(/\.$/, '')} — {discountOpen ? 'hide' : 'see'} pricing.
                        <ChevronDown
                          className="w-4 h-4 flex-shrink-0 transition-transform"
                          style={{ transform: discountOpen ? 'rotate(180deg)' : 'rotate(0deg)' }}
                        />
                      </span>
                    </button>
                  </div>

                  <div
                    id="paywall-discount-tiers"
                    ref={discountSectionRef}
                    className="overflow-hidden transition-[max-height,opacity] duration-500 ease-in-out"
                    style={{ maxHeight: discountOpen ? '5000px' : '0px', opacity: discountOpen ? 1 : 0 }}
                    aria-hidden={!discountOpen}
                  >
                    <p className="text-center text-[11px] uppercase tracking-[0.18em] mt-5 mb-4" style={{ color: 'var(--gold)' }}>
                      Dedicated tiers · same features · eligibility verified after subscribe
                    </p>
                    <div className="flex flex-wrap justify-center gap-4">
                      {discountPlans.map(p => renderPlanCard(p, true))}
                    </div>
                  </div>
                </div>
              )}
            </>
          );
        })()}
        </div>

        {/* Apple-required subscription disclosures */}
        <div className="mt-4 p-3 rounded-xl text-center space-y-1" style={{ background: 'var(--s)', border: '1px solid var(--b)' }}>
          <p className="text-[11px] text-[var(--t5)]">
            Subscriptions auto-renew monthly unless cancelled at least 24 hours before the end of the current billing period.
            Payment is charged to your {isNative ? 'Apple ID' : 'payment method'} at confirmation of purchase.
          </p>
          <p className="text-[11px] text-[var(--t5)]">
            You can manage or cancel your subscription anytime from {isNative ? 'iPhone Settings → Subscriptions' : 'Settings'}.
          </p>
          <div className="flex justify-center gap-3 pt-1">
            <a href="https://carryon.us/terms" target="_blank" rel="noopener noreferrer" className="text-[11px] text-[var(--gold)] font-bold">Terms of Service</a>
            <a href="https://carryon.us/privacy" target="_blank" rel="noopener noreferrer" className="text-[11px] text-[var(--gold)] font-bold">Privacy Policy</a>
            {isNative && (
              <button onClick={async () => { try { await restoreIAPPurchases(); if (refreshSubscription) refreshSubscription(); } catch {} }}
                className="text-[11px] text-[#3B82F6] font-bold">Restore Purchases</button>
            )}
          </div>
        </div>

        {/* Beneficiary: Family Plan Request */}
        {isBeneficiary && (
          <div className="mt-6 p-4 rounded-xl" style={{ background: 'rgba(96,165,250,0.05)', border: '1px solid rgba(96,165,250,0.12)' }}>
            <div className="flex items-start gap-3">
              <Users className="w-5 h-5 text-[#60A5FA] mt-0.5 shrink-0" />
              <div className="flex-1">
                <h4 className="text-sm font-semibold text-[var(--t)] mb-1">Part of a Family Plan?</h4>
                <p className="text-xs text-[var(--t5)] mb-3">
                  If a benefactor has a CarryOn Family Plan, ask them to add you — no separate subscription needed.
                </p>
                {showFamilyRequest ? (
                  <div className="flex gap-2">
                    <Input
                      value={familyEmail}
                      onChange={(e) => setFamilyEmail(e.target.value)}
                      placeholder="Benefactor's CarryOn email"
                      className="input-field text-xs h-9"
                      data-testid="family-request-email"
                    />
                    <Button
                      onClick={handleFamilyPlanRequest}
                      disabled={sendingRequest || !familyEmail.trim()}
                      className="h-9 px-4 text-xs font-bold shrink-0"
                      style={{ background: 'linear-gradient(135deg, #60A5FA, #3b82f6)', color: '#fff' }}
                      data-testid="send-family-request"
                    >
                      {sendingRequest ? <Loader2 className="w-3 h-3 animate-spin" /> : <Mail className="w-3 h-3 mr-1" />}
                      Send
                    </Button>
                    <Button onClick={() => setShowFamilyRequest(false)} variant="ghost" className="h-9 px-2 text-[var(--t5)]">
                      <X className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                ) : (
                  <Button
                    onClick={() => setShowFamilyRequest(true)}
                    className="text-xs h-8 px-4 font-semibold"
                    style={{ background: 'rgba(96,165,250,0.1)', color: '#60A5FA', border: '1px solid rgba(96,165,250,0.2)' }}
                    data-testid="request-family-plan"
                  >
                    <Users className="w-3 h-3 mr-1.5" /> Request Family Plan
                  </Button>
                )}
              </div>
            </div>
          </div>
        )}
      </CardContent>

      {/* Verification Upload Modal */}
      {showVerification && (
        <div className="fixed inset-0 z-[9999] bg-black/80 flex items-center justify-center p-4 overflow-y-auto" data-testid="verification-modal">
          <div className="w-full max-w-md rounded-2xl p-6 space-y-5 glass-card" style={{
            border: '1px solid var(--b)',
            boxShadow: '0 24px 64px rgba(0,0,0,0.6)',
          }}>
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-bold text-[var(--t)]" style={{ fontFamily: 'var(--sans)' }}>
                {tierDisplayName(planById(verificationTier)) || verificationTier} Verification
              </h2>
              <button onClick={() => { setShowVerification(false); setVerificationFile(null); setVerificationDocType(''); }}
                className="w-8 h-8 rounded-full bg-white/5 flex items-center justify-center text-[var(--t4)] active:scale-90 transition-transform">
                <X className="w-4 h-4" />
              </button>
            </div>

            <p className="text-sm text-[var(--t4)]">
              Upload one of the following to verify your eligibility:
            </p>

            <div className="space-y-2">
              <label className="text-xs text-[var(--t5)] font-medium">Document Type</label>
              <div className="flex flex-col gap-2">
                {verificationDocs(planById(verificationTier)).map(doc => (
                  <button
                    key={doc}
                    onClick={() => setVerificationDocType(doc)}
                    className="p-3 rounded-xl text-sm text-left transition-all"
                    style={{
                      background: verificationDocType === doc ? 'rgba(var(--gold-rgb), 0.1)' : 'var(--s)',
                      border: verificationDocType === doc ? '1px solid var(--gold)' : '1px solid var(--b)',
                      color: verificationDocType === doc ? 'var(--gold)' : 'var(--t3)',
                    }}
                    data-testid={`verify-doc-type-${doc.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    {doc}
                  </button>
                ))}
              </div>
            </div>

            <div className="space-y-2">
              <label className="text-xs text-[var(--t5)] font-medium">Upload Document</label>
              <label className="flex items-center justify-center gap-2 p-6 rounded-xl border-2 border-dashed cursor-pointer transition-colors"
                style={{ borderColor: verificationFile ? 'var(--gold)' : 'var(--b)' }}
                data-testid="verification-file-upload">
                <input type="file" accept="image/*,.pdf" className="hidden"
                  onChange={(e) => setVerificationFile(e.target.files[0])} />
                <Shield className="w-5 h-5 text-[var(--t5)]" />
                <span className="text-sm text-[var(--t4)]">
                  {verificationFile ? verificationFile.name : 'Click to select file'}
                </span>
              </label>
            </div>

            <Button
              onClick={handleVerificationUpload}
              disabled={uploadingVerification || !verificationFile || !verificationDocType}
              className="gold-button w-full"
              data-testid="submit-verification-btn"
            >
              {uploadingVerification ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Shield className="w-4 h-4 mr-2" />}
              Submit for Review
            </Button>

            <p className="text-[11px] text-[var(--t5)] text-center">
              Documents are reviewed within 24-48 hours. You'll be notified in your Customer Service portal once approved.
            </p>
          </div>
        </div>
      )}
    </Card>
  );
};
